import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { airportApi } from "../../../api/masterDataApi";

/**
 * useUldAirports — ULD 业务域内部使用的机场参考数据钩子。
 *
 * 历史背景：
 *   全局 hooks/useAirports 走的是 `/api/v3/schedule/drafts/reference/airports`，
 *   后端要求 `schedule:read` 权限。仅持有 `uld:*` 权限的用户访问会得到 403，
 *   导致 ULD 综合管理 / 历史 / 统计 / 位置批量更新等下拉框为空白
 *   （管理员账号能看到，普通 ULD 角色看不到）。
 *
 * 本钩子改走 `/api/v1/airports`（仅需登录，无业务域权限），与
 * `flightOperation/Monitor/MonitorContext` 的取数模式一致。
 *
 * 返回结构刻意对齐全局 useAirports 钩子，使 ULD 组件仅需替换 import。
 */

const PAGE_SIZE = 500;
const FETCH_PARAMS = { page: 1, page_size: PAGE_SIZE };

let cachedAirportsList = null;
let inflightPromise = null;

function extractErrorMessage(error, fallback = "获取机场数据失败") {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail) && detail.length > 0) {
        return detail[0]?.msg || fallback;
    }
    if (typeof detail === "string") {
        return detail;
    }
    return error?.message || fallback;
}

async function fetchAirportsOnce() {
    if (cachedAirportsList) {
        return cachedAirportsList;
    }
    if (inflightPromise) {
        return inflightPromise;
    }
    inflightPromise = airportApi
        .getAirportList(FETCH_PARAMS)
        .then((response) => {
            const items = response?.data?.items || [];
            cachedAirportsList = items;
            inflightPromise = null;
            return items;
        })
        .catch((error) => {
            inflightPromise = null;
            throw error;
        });
    return inflightPromise;
}

export function clearUldAirportsCache() {
    cachedAirportsList = null;
    inflightPromise = null;
}

export function useUldAirports() {
    const [airportsList, setAirportsList] = useState(cachedAirportsList || []);
    const [loading, setLoading] = useState(!cachedAirportsList);
    const [error, setError] = useState(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await fetchAirportsOnce();
            if (isMountedRef.current) {
                setAirportsList(list);
            }
        } catch (err) {
            if (isMountedRef.current) {
                setError(extractErrorMessage(err));
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (cachedAirportsList) {
            return;
        }
        load();
    }, [load]);

    const airportsMap = useMemo(
        () => airportsList.reduce((acc, airport) => {
            const code = airport.iata_code || airport.code || airport.airport_code;
            if (code) {
                acc[code] = airport;
            }
            return acc;
        }, {}),
        [airportsList],
    );

    const refreshAirports = useCallback(async () => {
        clearUldAirportsCache();
        await load();
    }, [load]);

    return {
        airports: airportsMap,
        airportsMap,
        airportsList,
        loading,
        isLoading: loading,
        error,
        refreshAirports,
    };
}

export default useUldAirports;
